"use client";

import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Copy, Download, Send, Check, Landmark, Zap, ShieldCheck, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

export function InvoiceDetailsModal({ 
  open,
  onOpenChange,
  invoice
}: { 
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: any;
}) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [viewTab, setViewTab] = useState("overview");

  if (!invoice) return null;

  const checkoutUrl = `https://checkout.paytaara.com/pay/${invoice.id.toLowerCase()}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(checkoutUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const lineItems = invoice.items || [
    { description: "Software Development Services", quantity: 1, unitPrice: invoice.subtotal || 250000 }
  ];

  const subtotal = invoice.subtotal || lineItems.reduce((acc: number, item: any) => acc + ((item.quantity || 1) * (item.unitPrice || 0)), 0);
  const taxAmount = invoice.tax || (subtotal * 0.075);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-3xl md:max-w-4xl lg:max-w-5xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        
        {/* Header & Tabs */}
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                <Zap className="h-5 w-5 fill-current" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-lg font-bold">
                    Invoice {invoice.id}
                  </DialogTitle>
                  <Badge className={cn(
                    "text-[10px] font-bold uppercase border-none px-2 py-0.5",
                    invoice.status === "Paid" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400" :
                    invoice.status === "Pending" ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400" :
                    invoice.status === "Overdue" ? "bg-destructive/10 text-destructive dark:bg-destructive/20" :
                    "bg-muted text-muted-foreground"
                  )}>
                    {invoice.status}
                  </Badge>
                </div>
                <DialogDescription className="text-xs mt-0.5">
                  Issued to <span className="font-semibold text-foreground">{invoice.customer}</span> ({invoice.email})
                </DialogDescription>
              </div>
            </div>

            <Tabs value={viewTab} onValueChange={setViewTab} className="w-full sm:w-auto">
              <TabsList className="grid grid-cols-2 h-9 bg-muted/60 p-1 border rounded-lg w-full sm:w-64">
                <TabsTrigger value="overview" className="text-xs font-bold h-7">
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Overview
                </TabsTrigger>
                <TabsTrigger value="pdf" className="text-xs font-bold h-7">
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF Preview
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </DialogHeader>

        <Tabs value={viewTab} onValueChange={setViewTab} className="pt-2">
          
          {/* TAB 1: OVERVIEW & DELEGATED NUBAN */}
          <TabsContent value="overview" className="space-y-6 m-0">
            
            {/* Checkout Payment Link & NUBAN Box */}
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                  <Landmark className="h-4 w-4 text-primary" /> Auto-Reconciled Dedicated Payment NUBAN
                </span>
                <Button variant="outline" size="sm" onClick={handleCopyLink} className="h-8 text-xs font-bold border-primary/30 text-primary hover:bg-primary/10">
                  {copiedLink ? <Check className="h-3.5 w-3.5 text-emerald-500 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                  {copiedLink ? "Checkout Link Copied" : "Copy Payment Link"}
                </Button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-background p-3.5 rounded-lg border font-mono text-xs">
                <div className="space-y-0.5">
                  <span className="text-[10px] text-muted-foreground font-sans block font-semibold">Bank Name</span>
                  <span className="font-bold text-foreground">Wema Bank PLC</span>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] text-muted-foreground font-sans block font-semibold">Virtual Account Number</span>
                  <span className="font-bold text-primary text-sm">0987654321</span>
                </div>
                <div className="space-y-0.5">
                  <span className="text-[10px] text-muted-foreground font-sans block font-semibold">Account Beneficiary</span>
                  <span className="font-bold text-foreground truncate block">PAYTAARA-{invoice.id}</span>
                </div>
              </div>
            </div>

            {/* Bill To & Date Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-4 rounded-xl bg-muted/20 border text-xs">
              <div className="space-y-1">
                <span className="text-muted-foreground block font-medium">Billed To</span>
                <span className="font-bold text-foreground block">{invoice.customer}</span>
                <span className="text-[11px] text-muted-foreground block">{invoice.email}</span>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground block font-medium">Issue Date</span>
                <span className="font-bold text-foreground block">{invoice.createdDate || "Oct 24, 2026"}</span>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground block font-medium">Payment Due Date</span>
                <span className="font-bold text-foreground block">{invoice.dueDate}</span>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground block font-medium">Total Amount Due</span>
                <span className="font-bold text-primary text-base block">{invoice.amount}</span>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Itemized Invoice Summary</h4>
              <div className="border rounded-xl overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/30">
                    <TableRow>
                      <TableHead className="text-xs font-bold min-w-[200px]">Item Description</TableHead>
                      <TableHead className="text-xs font-bold text-center">Qty</TableHead>
                      <TableHead className="text-xs font-bold text-right">Unit Price</TableHead>
                      <TableHead className="text-xs font-bold text-right">Subtotal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((item: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs font-medium">{item.description}</TableCell>
                        <TableCell className="text-xs font-mono text-center">{item.quantity}</TableCell>
                        <TableCell className="text-xs font-mono text-right">₦{(item.unitPrice || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-xs font-mono font-bold text-right">₦{((item.quantity || 1) * (item.unitPrice || 0)).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Summary Breakdown */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 rounded-xl bg-muted/10 border gap-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-emerald-500" /> Auto-settles directly into merchant ledger upon receipt.
              </div>
              <div className="w-full sm:w-64 space-y-1.5 text-xs font-mono">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal:</span>
                  <span>₦{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>VAT (7.5%):</span>
                  <span>+₦{taxAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-foreground pt-1 border-t">
                  <span>Total Due:</span>
                  <span className="text-primary">{invoice.amount}</span>
                </div>
              </div>
            </div>

          </TabsContent>

          {/* TAB 2: FORMAL PDF INVOICE PREVIEW CANVAS */}
          <TabsContent value="pdf" className="m-0">
            <div className="p-6 sm:p-8 rounded-xl bg-card border shadow-md space-y-6 text-foreground font-sans">
              
              {/* PDF Document Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-6">
                <div>
                  <div className="flex items-center gap-2 text-xl font-extrabold tracking-tight text-primary">
                    <Zap className="h-6 w-6 fill-current" /> PAYTAARA INVOICE
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">Paytaara Financial Infrastructure Ltd • Lagos, Nigeria</p>
                </div>
                <div className="text-left sm:text-right">
                  <h2 className="text-lg font-bold font-mono text-foreground">{invoice.id}</h2>
                  <p className="text-xs text-muted-foreground">Date: {invoice.createdDate || "Oct 24, 2026"}</p>
                  <p className="text-xs text-muted-foreground">Due: {invoice.dueDate}</p>
                </div>
              </div>

              {/* Billed To & Sender Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs">
                <div className="p-4 rounded-lg bg-muted/30 border space-y-1">
                  <span className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider block">Billed To</span>
                  <h3 className="text-sm font-bold text-foreground">{invoice.customer}</h3>
                  <p className="text-muted-foreground">{invoice.email}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 border space-y-1">
                  <span className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider block">Payment Instructions</span>
                  <p className="text-foreground font-medium">Bank Transfer via Dedicated NUBAN</p>
                  <p className="font-mono text-primary font-bold">Wema Bank • 0987654321</p>
                </div>
              </div>

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow>
                      <TableHead className="text-xs font-bold">Description</TableHead>
                      <TableHead className="text-xs font-bold text-center">Qty</TableHead>
                      <TableHead className="text-xs font-bold text-right">Unit Price</TableHead>
                      <TableHead className="text-xs font-bold text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((item: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{item.description}</TableCell>
                        <TableCell className="text-xs font-mono text-center">{item.quantity}</TableCell>
                        <TableCell className="text-xs font-mono text-right">₦{(item.unitPrice || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-xs font-mono font-bold text-right">₦{((item.quantity || 1) * (item.unitPrice || 0)).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* PDF Bottom Summary */}
              <div className="flex justify-end pt-2">
                <div className="w-full sm:w-64 space-y-2 text-xs">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal:</span>
                    <span className="font-mono font-semibold">₦{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>VAT (7.5%):</span>
                    <span className="font-mono font-semibold">+₦{taxAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-foreground pt-2 border-t">
                    <span>Grand Total:</span>
                    <span className="font-mono text-primary text-lg">{invoice.amount}</span>
                  </div>
                </div>
              </div>

            </div>
          </TabsContent>

        </Tabs>

        <DialogFooter className="pt-4 border-t flex flex-col sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" size="sm" onClick={() => alert("Downloading PDF document...")} className="h-9 text-xs font-semibold">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => alert("Reminder Email Sent!")} className="h-9 text-xs font-semibold">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Send Reminder
            </Button>
            <Button size="sm" onClick={() => onOpenChange(false)} className="h-9 px-5 text-xs font-semibold">
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
